import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IS_MOCK } from "../api";
import { ApiError } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import {
  type AuthMethods,
  fetchAuthMethods,
  passkeysSupported,
  PASSWORD_ONLY,
  startOAuth,
} from "../auth/methods";
import { Button, TextField } from "../compose/controls";
import { ArrowRight, KeyRound, Mail } from "../compose/Icon";
import { BrandWordmark } from "../shell/BrandWordmark";

/**
 * The demo account the in-memory fixture adapter accepts. Deliberately INLINED
 * rather than imported from `../api/fixtures`: `IS_MOCK` folds to a literal
 * `false` in a real build, so this string is dead code Rollup drops — whereas
 * an `import … from "../api/fixtures"` would drag the whole invented-staff
 * corpus into the production bundle. (Kept in sync by
 * `src/api/fixtures.test.ts`, which owns the fixture identities.)
 */
const DEMO_EMAIL = "dana@cerebr8.ai";

/**
 * Login `/login` — the first screen any visitor sees (logged-in-only app).
 *
 * The method row is SERVER-ADVERTISED: on mount the screen asks
 * `GET /api/auth/methods` which of password / passkey / Google / Microsoft /
 * email-link this deployment actually serves, and renders a provider button
 * ONLY when the answer is yes. An unserved provider is absent — never a
 * permanently `disabled` button that no environment variable could light up.
 * Google/Microsoft hand off to `/api/auth/oauth/:provider/start`; the email
 * link posts to `/api/auth/magic/request` (always 202 — no enumeration).
 * Brand cyanotype chrome, Newsreader wordmark (design screens §1).
 */
export function Login() {
  const { login, completeMfa, loginWithPasskey, requestMagic } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [email, setEmail] = useState(IS_MOCK ? DEMO_EMAIL : "");
  const [password, setPassword] = useState("");
  const [mfa, setMfa] = useState(false); // password ok → collect the TOTP code
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [methods, setMethods] = useState<AuthMethods>(PASSWORD_ONLY);

  // The capability probe. Pre-session by construction, so it runs before any
  // credential is typed; a failure leaves the password-only floor in place.
  useEffect(() => {
    let live = true;
    void fetchAuthMethods().then((m) => {
      if (live) setMethods(m);
    });
    return () => {
      live = false;
    };
  }, []);

  // Passkeys need BOTH a deployment that serves them and a platform that can do
  // WebAuthn; either missing means the control is absent, not inert.
  const canPasskey = methods.passkey && passkeysSupported();
  // Nothing to "continue with" ⇒ no divider, no empty method row.
  const alternatives =
    methods.google || methods.microsoft || canPasskey || methods.magic_link;

  function fail(err: unknown, fallback: string) {
    setError(
      err instanceof ApiError && err.status === 401
        ? fallback
        : "Sign-in failed. Please try again.",
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      const { mfaRequired } = await login(email.trim(), password);
      if (mfaRequired) {
        setMfa(true);
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      fail(err, "Those credentials didn't match. Check your email and password.");
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!code.trim()) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    try {
      await completeMfa(code.trim());
      navigate(from, { replace: true });
    } catch (err) {
      fail(err, "That code didn't verify. Try again or use a recovery code.");
    } finally {
      setBusy(false);
    }
  }

  async function passkey() {
    setError(null);
    setBusy(true);
    try {
      await loginWithPasskey();
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof Error && err.message === "passkey_cancelled") {
        setError(null);
      } else {
        fail(err, "Passkey sign-in failed. Try your password.");
      }
    } finally {
      setBusy(false);
    }
  }

  /** Email-link sign-in. The server answers 202 for every branch (issued /
   *  unknown / rate-limited), so the confirmation is deliberately identical
   *  whether or not the address exists — no account enumeration. */
  async function magicLink() {
    setError(null);
    setNotice(null);
    const addr = email.trim();
    if (!addr) {
      setError("Enter your email to get a sign-in link.");
      return;
    }
    setBusy(true);
    try {
      await requestMagic(addr);
      setNotice(`If ${addr} has an account, a sign-in link is on its way.`);
    } catch {
      setError("We couldn't send the link. Try your password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="auth__inner">
        <div className="auth__brand">
          <h1 className="auth__wordmark">
            <BrandWordmark className="auth__logo" height={46} />
          </h1>
          <div className="auth__tagline">Cerebr8&rsquo;s content operating system</div>
        </div>

        <div className="auth__card">
          {error ? (
            <div className="auth__error" role="alert" data-testid="login-error">
              {error}
            </div>
          ) : null}

          {mfa ? (
            <form onSubmit={submitCode} noValidate data-testid="mfa-form">
              <div className="auth__hint" style={{ marginBottom: 12 }}>
                Two-factor is on for this account. Enter the 6-digit code from
                your authenticator app (or a recovery code).
              </div>
              <TextField
                label="Authentication code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={code}
                data-testid="mfa-code"
                onChange={(e) => setCode(e.target.value)}
                autoFocus
              />
              <Button
                type="submit"
                variant="primary"
                block
                disabled={busy}
                data-testid="mfa-submit"
                icon={<ArrowRight size={16} strokeWidth={1.5} />}
              >
                {busy ? "Verifying…" : "Verify & sign in"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                block
                disabled={busy}
                data-testid="mfa-back"
                onClick={() => {
                  setMfa(false);
                  setCode("");
                  setError(null);
                }}
              >
                Back
              </Button>
            </form>
          ) : (
            <>
              <form onSubmit={submit} noValidate>
                <TextField
                  label="Email"
                  type="email"
                  placeholder="you@company.com"
                  autoComplete="username"
                  value={email}
                  data-testid="login-email"
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
                <TextField
                  label="Password"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  data-testid="login-password"
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="submit"
                  variant="primary"
                  block
                  disabled={busy}
                  data-testid="login-submit"
                  icon={<ArrowRight size={16} strokeWidth={1.5} />}
                >
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>

              {alternatives ? (
                <>
                  <div className="auth__divider">or continue with</div>
                  <div className="auth__methods">
                    {methods.google ? (
                      <Button
                        type="button"
                        variant="secondary"
                        title="Sign in with your Google account"
                        data-testid="login-google"
                        onClick={() => startOAuth("google")}
                      >
                        Google
                      </Button>
                    ) : null}
                    {methods.microsoft ? (
                      <Button
                        type="button"
                        variant="secondary"
                        title="Sign in with your Microsoft account"
                        data-testid="login-microsoft"
                        onClick={() => startOAuth("microsoft")}
                      >
                        Microsoft
                      </Button>
                    ) : null}
                    {canPasskey ? (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={busy}
                        title="Sign in with a passkey"
                        data-testid="login-passkey"
                        onClick={passkey}
                        icon={<KeyRound size={15} strokeWidth={1.5} />}
                      >
                        Passkey
                      </Button>
                    ) : null}
                    {methods.magic_link ? (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={busy}
                        title="Email me a single-use sign-in link"
                        data-testid="login-magic"
                        onClick={magicLink}
                        icon={<Mail size={15} strokeWidth={1.5} />}
                      >
                        Email link
                      </Button>
                    ) : null}
                  </div>
                </>
              ) : null}

              {notice ? (
                <div className="auth__hint" role="status" data-testid="login-notice">
                  {notice}
                </div>
              ) : null}

              {IS_MOCK ? (
                <div className="auth__hint" data-testid="login-hint">
                  Demo · sign in as <strong>{DEMO_EMAIL}</strong> with any password.
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="auth__foot">Logged-in only. No public access.</div>
      </div>
    </div>
  );
}
